import type { AxiosInstance } from "axios";

/**
 * What `GET /api/v2/customer-journey-orders` returns, and the helpers the standalone page
 * and the modal (opened from a report's Purchases cell) share.
 */

export type JourneySortKey =
  | "date"
  | "order_value"
  | "discount"
  | "session_count"
  | "total_ads"
  | "unique_ads"
  | "conversion_time"
  | "total_items"
  | "total_quantity";

export interface CustomerJourneyOrderItem {
  readonly order_id: string;
  readonly order_number: string;
  readonly email: string;
  readonly location: string;
  readonly created_at: string;
  readonly order_value: number;
  readonly discount: number;
  readonly financial_status: string;
  readonly order_status: string;
  readonly user_type: "New" | "Returning";
  readonly product_detail: string;
  readonly total_items: number;
  readonly total_quantity: number;
  readonly session_count: number;
  readonly total_ads: number;
  readonly unique_ads: number;
  readonly conversion_time_seconds: number;
  readonly first_touch_source: string;
  readonly first_touch_campaign: string;
  readonly last_touch_source: string;
  readonly last_touch_campaign: string;
  readonly touchpoint_sources: readonly string[];
  /** Parallel to `touchpoint_sources`: each touch's share of the order, 0..1 (non-zero only under a DDA / Linear / Triple scope). */
  readonly touchpoint_credits: readonly number[];
  readonly order_source: string;
}

export interface CustomerJourneyOrdersSummary {
  readonly totalOrders: number;
  readonly totalRevenue: number;
  readonly totalAdTouchpoints: number;
  readonly avgConversionSeconds: number;
}

export interface CustomerJourneyOrdersResponse {
  readonly from: string;
  readonly to: string;
  readonly orders: readonly CustomerJourneyOrderItem[];
  readonly summary: CustomerJourneyOrdersSummary;
  readonly totalOrderCount: number;
  readonly limit: number;
  readonly offset: number;
}

/** The attribution models a Purchases cell can open a journey list for. Mirrors the API's `model` enum. */
export type JourneyModel = "fc" | "lc" | "dda" | "linear" | "triple" | "single_session";

/** The Meta / Google campaign report drill levels. Mirrors the API's `level` enum. */
export type JourneyLevel = "campaign" | "adset" | "adgroup" | "ad" | "assetgroup" | "productgroup";

/** Sent as `entityId` for the "not matched to a campaign" bucket — legacy's own sentinel for it. */
export const JOURNEY_UNIDENTIFIED = "unidentified";

/** Which Purchases cell was clicked — everything the modal needs to ask for exactly those orders. */
export interface JourneyTarget {
  readonly model: JourneyModel;
  readonly source: string;
  /** What the modal's title calls it: a source name, a campaign name, "Not matched to a campaign". */
  readonly label: string;
  readonly level?: JourneyLevel;
  readonly entityId?: string;
  readonly parentCampaignId?: string;
  readonly parentAdsetId?: string;
}

export interface JourneyOrdersParams {
  readonly from: string;
  readonly to: string;
  readonly search: string;
  readonly sortBy: JourneySortKey;
  readonly direction: "asc" | "desc";
  readonly limit: number;
  readonly offset: number;
  readonly target?: JourneyTarget;
}

export async function fetchJourneyOrders(
  client: AxiosInstance,
  params: JourneyOrdersParams,
): Promise<CustomerJourneyOrdersResponse> {
  const { target, ...rest } = params;
  const res = await client.get<{ success: boolean; data: CustomerJourneyOrdersResponse }>(
    "/api/v2/customer-journey-orders",
    {
      params: {
        ...rest,
        // `undefined` params are dropped by axios, so a standalone call sends no scope at all.
        model: target?.model,
        source: target?.source,
        level: target?.level,
        entityId: target?.entityId,
        parentCampaignId: target?.parentCampaignId,
        parentAdsetId: target?.parentAdsetId,
      },
    },
  );
  return res.data.data;
}

/** Ported from legacy's `formatConversionTime` — the coarsest unit that isn't zero. */
export function formatConversionTime(seconds: number): string {
  if (seconds <= 0) return "—";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (days > 0) return `${String(days)} day${days === 1 ? "" : "s"}, ${String(hours)} hr`;
  if (hours > 0) return `${String(hours)} hr, ${String(minutes)} min`;
  return `${String(minutes)} min, ${String(secs)} sec`;
}

export function journeyMoney(value: number): string {
  return `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}
